import { computed } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { useAccountingStore } from '@/stores/accounting.store'

export const printTemplateSettingFields = Object.freeze([
  'layout',
  'accent_color',
  'font',
  'logo_position',
  'logo_size',
  'show_company_details',
  'zebra_rows',
  'totals_style',
  'highlight_payment_details',
  'show_powered_by',
])

export const printTemplateAccentColors = Object.freeze([
  '#A421BA', '#9333EA', '#DB2767', '#AD1457', '#1565C0', '#3949AB', '#00796B',
  '#2E7D32', '#E65100', '#C9A227', '#795548', '#37474F', '#212121',
])

export const pickPrintTemplateSettings = (template) => Object.fromEntries(printTemplateSettingFields.map(field => [field, template[field]]))

export const usePrintTemplates = () => {
  const accountingStore = useAccountingStore()
  const { QUOTE, INVOICE, REPORT } = accountingStore.printDocumentTypeEnum
  const defaultFields = Object.freeze({ [QUOTE]: 'default_quote_print_template', [INVOICE]: 'default_invoice_print_template', [REPORT]: 'default_report_print_template' })
  const documentTypeLabelKeys = Object.freeze({ [QUOTE]: 'common.quote', [INVOICE]: 'common.invoice', [REPORT]: 'common.report.report' })
  const setDefaultLabelKeys = Object.freeze({ [QUOTE]: 'common.printTemplateSetAsQuoteDefault', [INVOICE]: 'common.printTemplateSetAsInvoiceDefault', [REPORT]: 'common.printTemplateSetAsReportDefault' })

  accountingStore.ensureDocumentPrintTemplatesAsync()

  const templates = computed(() => accountingStore.getLocalizedDocumentPrintTemplates())
  const layouts = computed(() => accountingStore.getLocalizedPrintLayouts())

  const templateById = (id) => templates.value.find(template => template.id === id)
  const defaultIdFor = (documentType) => accountingStore.companyData[defaultFields[documentType]] ?? null
  const isDefaultFor = (template, documentType) => template.id === defaultIdFor(documentType)
  const isDefault = (template) => Object.keys(defaultFields).some(documentType => isDefaultFor(template, Number(documentType)))
  const selectableTemplates = (selectedId) => templates.value.filter(template => template.is_available || template.id === selectedId)
  const layoutDescription = (template) => layouts.value.find(layout => layout.id === template.layout)?.description
  const defaultDocumentTypes = (template) => [QUOTE, INVOICE, REPORT].filter(documentType => isDefaultFor(template, documentType))
  const documentTypeLabel = (documentType) => getI18n().t(documentTypeLabelKeys[documentType], 2)
  const setDefaultLabel = (documentType) => getI18n().t(setDefaultLabelKeys[documentType])

  return { templates, layouts, defaultFields, templateById, defaultIdFor, isDefaultFor, isDefault, selectableTemplates, layoutDescription, defaultDocumentTypes, documentTypeLabel, setDefaultLabel }
}
